<template>
  <div class="flex w-full flex-col">
    <div class="bg-surface-container-high" :class="[expanded && 'sticky top-0 z-20']">
      <div
        class="divide-outline-variant relative flex h-16 flex-row items-center divide-x overflow-hidden transition-all"
        :class="[
          !selected && 'bg-surface-container-low rounded-medium',
          selected && 'bg-secondary-container rounded-large',
          expanded && 'rounded-b-extra-small!',
        ]"
      >
        <button
          class="group relative h-full overflow-hidden px-2"
          :disabled="disabled"
          @click="emits('toggleSelected')"
        >
          <BaseRadioButton v-if="singleChoice" :is-checked="selected" :disabled="disabled" />
          <BaseCheckbox v-else :is-checked="selected" :disabled="disabled" />
          <BaseStateLayer v-if="!disabled" />
        </button>
        <button
          class="group relative flex h-full grow flex-row items-center overflow-hidden px-4 text-left"
          :class="[disabled && 'opacity-70']"
          :disabled="disabled"
          @click="toggleExpanded"
        >
          <div class="flex grow flex-col">
            <span class="text-body-large text-on-surface">{{ questionGroup.title }}</span>
            <span v-if="supportingText" class="text-body-medium text-on-surface-variant">{{
              supportingText
            }}</span>
          </div>
          <span class="text-label-medium text-on-surface-variant whitespace-nowrap">
            {{ numberOfQuestions }} {{ $t('common.question', { count: numberOfQuestions }) }}</span
          >
          <BaseIcon
            v-if="!disabled"
            class="text-on-surface-variant ml-1"
            :class="[expanded && 'rotate-180']"
            >keyboard_arrow_down</BaseIcon
          >
          <BaseStateLayer v-if="!disabled" />
        </button>
      </div>
    </div>
    <BaseAnimatedExtend :is-extended="expanded">
      <div class="pt-0.5">
        <div
          class="bg-surface-container-low rounded-t-extra-small w-full pt-4"
          :class="[!selected && 'rounded-b-medium', selected && 'rounded-b-large']"
        >
          <div class="flex w-full flex-row justify-end px-4 pb-2">
            <BaseTextButton
              color="filled"
              @click="emits('toggleSelected')"
              :disabled="disabled"
              :toggle="selected"
              :selected="selected"
              :icon="selected ? 'check' : undefined"
              >{{ selected ? $t('common.selected') : $t('common.select') }}</BaseTextButton
            >
          </div>
          <ul class="divide-outline-variant flex flex-col gap-0.5 divide-y">
            <li
              v-for="(question, index) in questionGroup.questions"
              :key="question.id"
              class="text-body-medium flex flex-row px-4 py-2"
            >
              <span class="text-outline w-6 shrink-0">
                {{ index + 1 }}
              </span>
              <p class="">
                {{ question.content }}
              </p>
            </li>
          </ul>
        </div>
      </div>
    </BaseAnimatedExtend>
  </div>
</template>

<script setup lang="ts">
  import BaseCheckbox from '../base/checkbox/BaseCheckbox.vue'
  import BaseIcon from '../base/icon/BaseIcon.vue'
  import { computed } from 'vue'
  import BaseAnimatedExtend from '../base/animated-extend/BaseAnimatedExtend.vue'
  import BaseTextButton from '../base/button/text-button/BaseTextButton.vue'
  import BaseStateLayer from '../base/state-layer/BaseStateLayer.vue'
  import BaseRadioButton from '../base/radio-button/BaseRadioButton.vue'
  import type { QuestionGroup } from '@/modules/give-feedback/models/question-group.ts'

  const props = defineProps<Props>()

  interface Props {
    supportingText?: string
    questionGroup: QuestionGroup
    expanded: boolean
    selected: boolean
    disabled?: boolean
    singleChoice?: boolean
  }

  const emits = defineEmits<{
    (e: 'toggleSelected'): void
    (e: 'toggleExpanded'): void
  }>()

  const numberOfQuestions = computed(() => props.questionGroup.questions.length)

  const toggleExpanded = () => {
    if (props.disabled) return
    emits('toggleExpanded')
  }
</script>
